import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useAuth } from "../context/AuthContext";
import { getRefCookie } from "../App";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
    license_type: "gym",
    is_affiliate: false,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const ref = getRefCookie();
      await register({ ...form, referred_by: ref || null, is_affiliate: !!form.is_affiliate });
      navigate("/dashboard");
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-16" data-testid="register-page">
      <div className="w-full max-w-lg glass rounded-3xl p-8 sm:p-10">
        <span className="eyebrow">License Holder</span>
        <h1 className="font-display tracking-tight text-3xl mt-4">Create your Fitest account</h1>
        <p className="text-sm text-slate-400 mt-2">You'll be able to start generating audit links immediately.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-300">Name</Label>
              <Input required value={form.name} onChange={update("name")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="register-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-300">Company</Label>
              <Input value={form.company} onChange={update("company")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="register-company" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-300">Email</Label>
            <Input type="email" required value={form.email} onChange={update("email")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="register-email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-300">Password (min 8)</Label>
            <Input type="password" required minLength={8} value={form.password} onChange={update("password")} className="bg-white/5 border-white/10 mt-1 h-11" data-testid="register-password" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-300 mb-2 block">License type</Label>
            <RadioGroup
              value={form.license_type}
              onValueChange={(v) => setForm({ ...form, license_type: v })}
              className="grid grid-cols-2 gap-3"
              data-testid="register-license-type"
            >
              <label className={`cursor-pointer rounded-xl p-4 border ${form.license_type === "gym" ? "border-[var(--border-active)] bg-white/5" : "border-white/10"}`}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="gym" id="lt-gym" />
                  <span className="font-display">Gym</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Member retention focus</p>
              </label>
              <label className={`cursor-pointer rounded-xl p-4 border ${form.license_type === "business" ? "border-[var(--border-active)] bg-white/5" : "border-white/10"}`}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="business" id="lt-biz" />
                  <span className="font-display">Business</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Workforce reporting</p>
              </label>
            </RadioGroup>
          </div>

          {err && <p className="text-sm text-red-400" data-testid="register-error">{err}</p>}

          <label className="flex items-start gap-3 cursor-pointer pt-2" data-testid="register-affiliate-toggle">
            <input
              type="checkbox"
              checked={form.is_affiliate}
              onChange={(e) => setForm({ ...form, is_affiliate: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#00BFFF]"
              data-testid="register-affiliate-checkbox"
            />
            <span className="text-sm text-slate-300">
              I'm signing up as an <span className="text-white">affiliate partner</span> — I'll earn 20% recurring on every licence I refer.
            </span>
          </label>

          <Button type="submit" disabled={loading} className="btn-glow w-full rounded-full h-12" data-testid="register-submit">
            {loading ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-slate-400 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-brand hover:underline" data-testid="register-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
